import { Injectable, computed, signal } from "@angular/core";
import { BehaviorSubject, type Observable } from "rxjs";
import { distinctUntilChanged } from "rxjs/operators";


import {
  AUTH0_ROLES,
  DAVACO_ROLES,
  AUTH_SOURCES,
} from "../config/app.constants";
import { decodeJwtPayload } from "../auth/decode-jwt-payload";
import { environment } from "../../../environments/environment";
import { deriveUserProfile } from "./user-profile.util";

import type { AccountInfo } from "@azure/msal-browser";
import type {
  IAuthUser,
  INormalizedClaims,
  ITokenPayload,
} from "../models/auth.model";
import type { IUserProfile } from "../models/i-user-profile";

/**
 * Holds the app's authenticated-user state. User state is derived from an MSAL
 * account's id-token claims via `setFromAccount` (called by the active
 * IAuthProvider). This service no longer parses raw JWTs or touches
 * localStorage — MSAL owns the token cache.
 */
@Injectable({ providedIn: "root" })
export class AuthService {
  private readonly currentUserSubject = new BehaviorSubject<IAuthUser | null>(
    null
  );

  private readonly userSignal = signal<IAuthUser | null>(null);

  private readonly avatarSignal = signal<string | null>(null);

  readonly avatarUrl = this.avatarSignal.asReadonly();

  readonly userProfile = computed<IUserProfile>(() =>
    deriveUserProfile(this.userSignal()?.claims ?? null)
  );

  readonly currentUser$: Observable<IAuthUser | null> = this.currentUserSubject
    .asObservable()
    .pipe(distinctUntilChanged());

  getCurrentUser(): Observable<IAuthUser | null> {
    return this.currentUser$;
  }

  isAuthenticated(): boolean {
    const user = this.currentUserSubject.value;
    return !!user && user.isAuthenticated;
  }

  /**
   * True when an account is signed in, regardless of role authorization.
   * Lets the guard distinguish "signed in but no role" (→ /unauthorized) from
   * "no account" (→ /login).
   */
  hasAccount(): boolean {
    return this.currentUserSubject.value !== null;
  }

  hasRole(role: string): boolean {
    const user = this.currentUserSubject.value;
    return !!user && user.claims.roles.includes(role);
  }

  isDavacoAdmin(): boolean {
    return !!this.currentUserSubject.value?.isDavacoAdmin;
  }

  isSpOwner(): boolean {
    return !!this.currentUserSubject.value?.isSpOwner;
  }

  isSpAdmin(): boolean {
    return !!this.currentUserSubject.value?.isSpAdmin;
  }

  isSpEmployee(): boolean {
    return !!this.currentUserSubject.value?.isSpEmployee;
  }

  setAvatarUrl(url: string | null): void {
    this.avatarSignal.set(url);
  }

  /**
   * Derives and publishes the current user from an MSAL account.
   *
   * `apiAccessToken` is the access token for the SP API. Pass it whenever one
   * is available, because THE ROLES ARE ONLY IN THERE. Azure AD puts `roles`
   * in a token based on app roles defined on the registration that token is
   * for: the SP API registration defines ct-admin / sp-owner / sp-admin /
   * sp-user and holds the real user assignments, while the SPA registration
   * this app signs into defines none. So the id token carries no roles at all,
   * and reading it alone makes every user look unauthorized -- which is exactly
   * what happened in production, where `buildAuthUser` requires at least one
   * role. The id token remains the fallback for the Auth0 provider, whose
   * roles arrive on the profile claims instead.
   *
   * Caller must publish ONCE with the token already in hand. Publishing a
   * role-less user first and refreshing after the token resolves leaves a
   * window in which the route guard sees no roles and denies access.
   */
  setFromAccount(account: AccountInfo, apiAccessToken?: string | null): void {
    const idClaims = (account.idTokenClaims ?? {}) as ITokenPayload;
    const accessClaims = decodeJwtPayload(apiAccessToken);
    const claims = this.normalizeClaims(idClaims, accessClaims);
    const user = this.buildAuthUser(claims);
    this.userSignal.set(user);
    this.currentUserSubject.next(user);
  }

  /** Clears the published user state. */
  clearToken(): void {
    this.userSignal.set(null);
    this.currentUserSubject.next(null);
    this.avatarSignal.set(null);
  }

  /** Clears app user state (the IAuthProvider performs the IdP redirect). */
  logout(): void {
    this.clearToken();
  }

  private normalizeClaims(
    payload: ITokenPayload,
    accessClaims: Record<string, unknown> | null = null
  ): INormalizedClaims {
    let authSource: "aad" | "auth0" = AUTH_SOURCES.AUTH0;
    if (payload.iss && payload.iss.includes("login.microsoftonline.com")) {
      authSource = AUTH_SOURCES.AAD;
    }

    let subjectId = "";
    if (authSource === AUTH_SOURCES.AAD && payload.oid) {
      subjectId = payload.oid;
    } else if (payload.sub) {
      subjectId = payload.sub;
    }

    // Access token first (it is the only place AAD roles exist -- see
    // setFromAccount), then the id token for the Auth0 path.
    const roles =
      AuthService.readRoles(accessClaims?.["roles"]) ??
      AuthService.readRoles(payload.roles) ??
      [];

    return {
      subjectId,
      authSource,
      roles,
      email: payload.email,
      name: payload.name,
      givenName: payload.given_name,
      familyName: payload.family_name,
    };
  }

  /**
   * Normalizes a `roles` claim. Returns null when the claim is absent, which
   * lets the caller fall through to the next source -- distinct from an empty
   * array, which means "present and genuinely empty".
   */
  private static readRoles(value: unknown): string[] | null {
    if (Array.isArray(value)) {
      return value.filter((v): v is string => typeof v === "string");
    }
    if (typeof value === "string" && value.length > 0) {
      return [value];
    }
    return null;
  }

  private buildAuthUser(claims: INormalizedClaims): IAuthUser {
    const roles = claims.roles || [];
    const hasRoles = roles.length > 0;
    return {
      claims,
      // Prod requires at least one role claim; dev allows role-less accounts.
      isAuthenticated:
        !!claims.subjectId && (hasRoles || !environment.production),
      isDavacoAdmin: roles.includes(DAVACO_ROLES.DAVACO_ADMIN),
      isSpOwner: roles.includes(AUTH0_ROLES.SP_OWNER),
      isSpAdmin: roles.includes(AUTH0_ROLES.SP_ADMIN),
      isSpEmployee: roles.includes(AUTH0_ROLES.SP_EMPLOYEE),
    };
  }
}
